import { Button, ButtonRole, ButtonTargetKind } from "@components/Button";
import { Icon, IconSize, IconDisplay } from "@components/Icon";
import { Popover } from "@components/Popover";
import { PopoverProps } from "@components/Popover/types";
import { css } from "@emotion/react";
import type { CSSInterpolation } from "@emotion/serialize";
import React from "react";

import { useOnClickOutside } from "src/hooks/useOnClickOutside";
import { FundraiserNonprofitsList } from "src/pages/Fundraiser/FundraiserNonprofitsList";
import { Divider } from "src/pages/NewDonateButtonPage/style";
import { colorCssVars } from "src/theme/color";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { FontWeight, textSizeCss } from "src/theme/text";

interface FundraiserForNonprofitsPopoverProps {
  nonprofitIds: string[];
  className?: string;
  contentCss?: CSSInterpolation;
  showFooter?: boolean;
  weights?: number[];
  placement?: PopoverProps["placement"];
  checkedIds?: string[];
  onToggle?: (id: string) => void;
}

export const FundraiserNonprofitsPopover = (
  props: FundraiserForNonprofitsPopoverProps
) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const popoverButtonRef = React.useRef<HTMLButtonElement>(null);
  const popoverContentRef = useOnClickOutside<HTMLDivElement>({
    handleClickOutside: (event) => {
      if (
        event &&
        popoverButtonRef.current &&
        popoverButtonRef.current.contains(event.target as Node)
      ) {
        return;
      }
      setIsOpen(false);
    },
  });

  if (!props.nonprofitIds || props.nonprofitIds.length === 0) {
    return null;
  }

  return (
    <Popover
      visible={isOpen}
      placement={props.placement ?? "bottom-start"}
      className={props.className}
      css={{ display: "inline-flex", width: "fit-content" }}
      contentCss={props.contentCss}
      content={
        <div
          css={{ maxWidth: "400px", padding: spacing.xs }}
          ref={popoverContentRef}
        >
          <FundraiserNonprofitsList {...props} />
          {props.showFooter && (
            <React.Fragment>
              <Divider css={{ margin: `${spacing.m} 0` }} />
              <div css={verticalStackCss.xxs}>
                <div css={[horizontalStackCss.xs, { alignItems: "center" }]}>
                  <Icon
                    iconImport={() =>
                      import("@components/Icon/icons/ThumbsUpIcon")
                    }
                    size={IconSize.MEDIUM}
                    display={IconDisplay.ACCENT}
                  />
                  <h5 css={textSizeCss.s}>Equal distribution</h5>
                </div>
                <p
                  css={[
                    textSizeCss.xs,
                    {
                      marginTop: spacing.xs,
                      color: `var(${colorCssVars.text.secondary})`,
                    },
                  ]}
                >
                  The funds raised from this fundraiser will be distributed
                  evenly among the nonprofit organizations.
                </p>
              </div>
            </React.Fragment>
          )}
        </div>
      }
    >
      <Button
        ref={popoverButtonRef}
        data-tname="fundraiser-nonprofits"
        role={ButtonRole.UNSTYLED}
        contentCss={css`
          ${horizontalStackCss.xxxs};
          align-items: center;
          color: var(${colorCssVars.text.body});
          font-weight: ${FontWeight.MEDIUM};
          transition: color 0.2s ease-in-out;

          &:hover {
            color: var(${colorCssVars.text.secondary});
          }
        `}
        onClick={{
          kind: ButtonTargetKind.FUNCTION,
          action: () => setIsOpen((prev) => !prev),
        }}
      >
        <span>
          {props.checkedIds?.length ?? props.nonprofitIds.length} nonprofits
        </span>{" "}
        <Icon
          css={
            isOpen &&
            css`
              position: relative;
              top: -1px;
              transform: rotate(180deg);
            `
          }
          iconImport={() => import("@components/Icon/icons/SelectArrowIcon")}
          size={IconSize.X_SMALL}
          display={IconDisplay.SECONDARY}
        />
      </Button>
    </Popover>
  );
};
